import { isOutstandingInvoice, outstandingRemainingCents } from '@/lib/outstandingInvoice';
import { cashierBucketKey } from './cashierEngine';
import { CLINIC_TIMEZONE, isLocalNoon } from './timezone';
import type {
  CashierCreditNoteRow,
  CashierFilterState,
  CashierOutstandingInvoice,
  CashierPaymentRow,
  CashierReconciliation,
  CashierReportResult,
  CashierServicedRow,
} from './cashierTypes';

export interface CashierDayVisit {
  id: string;
  clinicianId: string | null;
  clinicianName: string | null;
  serviceName: string | null;
  patientName: string | null;
  completedAt: string | null;
}

export interface CashierDayInvoice {
  id: string;
  invoiceNumber: string | null;
  kind: string | null;
  status: string;
  totalCents: number;
  paidCents: number;
  issuedAt: string | null;
  dueAt: string | null;
  appointmentId: string | null;
  creditsInvoiceId: string | null;
  patientName: string | null;
  clinicId: string;
  /** False when the row was loaded only to name a credit note's original invoice. */
  inPeriod?: boolean;
}

export interface CashierDayLine {
  invoiceId: string;
  description: string;
  lineTotalCents: number;
  source: string | null;
}

export interface CashierDayPayment {
  id: string;
  invoiceId: string;
  amountCents: number;
  createdAt: string | null;
}

export interface CashierDaySource {
  visits: CashierDayVisit[];
  invoices: CashierDayInvoice[];
  lines: CashierDayLine[];
  /** Payments on the period's invoices, including those taken outside the period. */
  payments: CashierDayPayment[];
  recordedAtByPaymentId: Record<string, string>;
}

export interface CashierDayDetail {
  servicedRows: CashierServicedRow[];
  creditNotes: CashierCreditNoteRow[];
  outstandingInvoices: CashierOutstandingInvoice[];
  reconciliation: CashierReconciliation;
  recordedAtByPaymentId: Record<string, string>;
}

function matchesSearch(
  filters: Pick<CashierFilterState, 'search'>,
  ...parts: Array<string | null>
): boolean {
  const query = filters.search.trim().toLowerCase();
  if (!query) return true;
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(query);
}

/** Invoices issued in the period. Credit notes are listed separately and never as outstanding. */
export function buildCashierDay(
  source: CashierDaySource,
  filters: Pick<CashierFilterState, 'practitionerIds' | 'search'> = {
    practitionerIds: [],
    search: '',
  },
): CashierDayDetail {
  const visits = new Map(source.visits.map((visit) => [visit.id, visit]));
  const invoices = new Map(source.invoices.map((invoice) => [invoice.id, invoice]));
  const linesByInvoice = new Map<string, CashierDayLine[]>();
  for (const line of source.lines) {
    const list = linesByInvoice.get(line.invoiceId) ?? [];
    list.push(line);
    linesByInvoice.set(line.invoiceId, list);
  }

  const servicedInvoices = source.invoices.filter((invoice) => {
    if (invoice.kind === 'credit_note' || invoice.inPeriod === false) return false;
    const visit = invoice.appointmentId ? visits.get(invoice.appointmentId) : null;
    if (filters.practitionerIds.length > 0) {
      const clinicianId = visit?.clinicianId ?? null;
      if (!clinicianId || !filters.practitionerIds.includes(clinicianId)) return false;
    }
    return matchesSearch(filters, invoice.patientName, invoice.invoiceNumber, visit?.serviceName ?? null);
  });

  const servicedRows: CashierServicedRow[] = servicedInvoices.map((invoice) => {
    const visit = invoice.appointmentId ? visits.get(invoice.appointmentId) : null;
    const lines = linesByInvoice.get(invoice.id) ?? [];
    const positive = lines
      .filter((line) => line.source !== 'punch_card_redemption')
      .reduce((sum, line) => sum + line.lineTotalCents, 0);
    const clip = lines
      .filter((line) => line.source === 'punch_card_redemption')
      .reduce((sum, line) => sum + Math.abs(line.lineTotalCents), 0);
    const serviceName =
      visit?.serviceName ??
      lines.find((line) => line.source !== 'punch_card_redemption')?.description ??
      null;
    return {
      id: invoice.id,
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      patientName: invoice.patientName ?? visit?.patientName ?? null,
      practitionerName: visit?.clinicianName ?? null,
      serviceName,
      amountCents: positive > 0 ? positive : invoice.totalCents,
      clipCents: clip,
    };
  });

  const servicedIds = new Set(servicedRows.map((row) => row.id));
  const creditNotes: CashierCreditNoteRow[] = source.invoices
    .filter((invoice) => invoice.kind === 'credit_note' && invoice.inPeriod !== false)
    .filter((invoice) =>
      matchesSearch(
        filters,
        invoice.patientName,
        invoice.invoiceNumber,
        invoice.creditsInvoiceId ? invoices.get(invoice.creditsInvoiceId)?.invoiceNumber ?? null : null,
      ),
    )
    .map((invoice) => {
      const original = invoice.creditsInvoiceId ? invoices.get(invoice.creditsInvoiceId) : null;
      return {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        originalInvoiceId: invoice.creditsInvoiceId,
        originalInvoiceNumber: original?.invoiceNumber ?? null,
        patientName: invoice.patientName ?? original?.patientName ?? null,
        amountCents: invoice.totalCents,
        issuedAt: invoice.issuedAt,
      };
    });

  let paidCents = 0;
  let refundedCents = 0;
  for (const payment of source.payments) {
    if (!servicedIds.has(payment.invoiceId)) continue;
    if (payment.amountCents < 0) refundedCents += Math.abs(payment.amountCents);
    else paidCents += payment.amountCents;
  }

  const outstandingInvoices = servicedInvoices.flatMap((invoice) => {
    if (
      !isOutstandingInvoice({
        kind: invoice.kind,
        status: invoice.status,
        totalCents: invoice.totalCents,
        paidCents: invoice.paidCents,
      })
    ) {
      return [];
    }
    const visit = invoice.appointmentId ? visits.get(invoice.appointmentId) : null;
    return [
      {
        id: invoice.id,
        invoice_number: invoice.invoiceNumber,
        patient_id: null,
        patient_name: invoice.patientName ?? visit?.patientName ?? null,
        issued_at: invoice.issuedAt,
        due_at: invoice.dueAt,
        total_cents: invoice.totalCents,
        paid_cents: invoice.paidCents,
        outstanding_cents: outstandingRemainingCents(invoice.totalCents, invoice.paidCents),
        status: invoice.status,
        clinic_id: invoice.clinicId,
        clinic_name: null,
        related_practitioner_ids: visit?.clinicianId ? [visit.clinicianId] : [],
      },
    ];
  });

  const reconciliation: CashierReconciliation = {
    servicedCents: servicedRows.reduce((sum, row) => sum + row.amountCents, 0),
    creditedCents: creditNotes.reduce((sum, row) => sum + row.amountCents, 0),
    paidCents,
    refundedCents,
    clipCents: servicedRows.reduce((sum, row) => sum + row.clipCents, 0),
    outstandingCents: outstandingInvoices.reduce((sum, row) => sum + row.outstanding_cents, 0),
  };

  return {
    servicedRows,
    creditNotes,
    outstandingInvoices,
    reconciliation,
    recordedAtByPaymentId: source.recordedAtByPaymentId,
  };
}

export function reconciliationBalances(line: CashierReconciliation): boolean {
  const left = line.servicedCents - line.creditedCents;
  const right = line.paidCents - line.refundedCents + line.clipCents + line.outstandingCents;
  return left === right;
}

export function applyCashierDay(result: CashierReportResult, day: CashierDayDetail): CashierReportResult {
  const transactions = result.transactions.map((row) => ({
    ...row,
    recorded_at: day.recordedAtByPaymentId[row.id] ?? row.recorded_at ?? null,
  }));
  const summary = {
    ...result.summary,
    outstanding_cents: day.reconciliation.outstandingCents,
    outstanding_count: day.outstandingInvoices.length,
  };

  return {
    ...result,
    summary,
    outstandingInvoices: day.outstandingInvoices,
    transactions,
    periodRows: periodRowsAtRealTime(transactions, result.grain, result.timezone),
    servicedRows: day.servicedRows,
    creditNotes: day.creditNotes,
    reconciliation: day.reconciliation,
    empty:
      transactions.length === 0 &&
      summary.transaction_count === 0 &&
      summary.refund_count === 0 &&
      day.outstandingInvoices.length === 0 &&
      day.servicedRows.length === 0 &&
      day.creditNotes.length === 0,
  };
}

/** Noon means the date field had no clock. The row's created_at is the registration time. */
export function paymentDisplayTime(row: CashierPaymentRow, timeZone = CLINIC_TIMEZONE): string {
  if (row.recorded_at && isLocalNoon(row.payment_date, timeZone)) return row.recorded_at;
  return row.payment_date;
}

function periodRowsAtRealTime(
  transactions: CashierPaymentRow[],
  grain: CashierReportResult['grain'],
  timeZone: string,
): CashierReportResult['periodRows'] {
  const periodMap = new Map<string, CashierReportResult['periodRows'][number]>();
  for (const row of transactions) {
    const bucket = cashierBucketKey(paymentDisplayTime(row, timeZone), grain, timeZone);
    const periodRow = periodMap.get(bucket) ?? {
      bucket,
      label: bucket,
      received_cents: 0,
      refund_cents: 0,
      net_cents: 0,
      transaction_count: 0,
      refund_count: 0,
    };
    if (row.kind === 'payment') {
      periodRow.received_cents += row.amount_cents;
      periodRow.transaction_count += 1;
    } else {
      periodRow.refund_cents += Math.abs(row.amount_cents);
      periodRow.refund_count += 1;
    }
    periodRow.net_cents = periodRow.received_cents - periodRow.refund_cents;
    periodMap.set(bucket, periodRow);
  }
  return [...periodMap.values()].sort((a, b) => a.bucket.localeCompare(b.bucket));
}
